<template>
  <div>
    <ServiceScreenLayout :parts="parts">
      <template v-slot:call>
        <PricingFilterToolbar
          :search-term="recordSearchTerm"
          :expired-filter="recordExpiredFilter"
          search-placeholder="Search by Description or Label..."
          @search-change="recordSearchTerm = $event"
          @expired-filter-change="recordExpiredFilter = $event"
        >
          <template v-slot:actions>
            <button
              v-if="userEditRight"
              type="button"
              class="btn btn-info mr-3 mb-2"
              @click="createNewRecording()"
            >
              Create New Package
            </button>
          </template>
        </PricingFilterToolbar>
          <div class="row row-spacing mb-3">
            <div class="col-md-12">
              <div
                class="table-responsive table-bordered"
                v-if="recordingPackages.length > 0"
              >
                <table class="table pricing-table">
                  <thead>
                    <tr class="text-nowrap">
                      <th class="pricing-column-description">Description</th>
                      <th class="pricing-column-label">Label</th>
                      <th class="pricing-column-count">Hours</th>
                      <th class="pricing-column-price">MRC (excl. VAT)</th>
                      <th class="pricing-column-status">Expired</th>
                      <th class="pricing-column-actions"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      :class="{ 'bg-primary': record.expired }"
                      v-for="record in recordingPackages"
                      :key="record.callCabinetPackageNumber"
                    >
                      <td>{{ record.description }}</td>
                      <td class="pricing-column-label">{{ record.label }}</td>
                      <td>{{ record.hours }}</td>
                      <td>
                        {{ getPrice(record.prices, period) }}
                        <span
                          v-if="userEditRight"                  
                          style="cursor: pointer"
                          @click="openRecordingPriceModal(record)"
                          v-tooltip="'Edit Row Price'"
                          class="pull-right"
                        >
                          <em class="fa fa-edit fa-fw text-green"></em>
                        </span>
                      </td>
                      <td class="text-center">
                        <span
                          class="badge"
                          :class="
                            record.expired ? 'badge-warning' : 'badge-green'
                          "
                          >{{ record.expired }}
                        </span>
                      </td>
                      <td class="text-center">
                        <button
                          v-if="userEditRight"                  
                          v-tooltip="'Edit Package'"
                          @click="editRecording(record)"
                          type="button"
                          class="btn btn-sm btn-info m-1"
                        >
                          <em class="fa fa-edit fa-fw"></em>
                        </button>
                        <button
                          v-if="userEditRight"                  
                          v-tooltip="'Delete Package'"
                          type="button"
                          @click="deletePackage(record)"
                          class="btn btn-sm btn-primary"
                        >
                          <em class="fa fa-trash fa-fw"></em>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-else class="alert alert-info">No Call Cabinets to Display</div>
            </div>
          </div>
          <ModifyCx3RecordingPackageModal
            ref="modifyRecordingPackageModal"
            :pricePackage="priceRecordingPackage"
            @packageAdded="recordingAdded"
            @packageEdited="loadCx3Recording()"
          />
          <AddPriceModal
            ref="addRecordingPriceModal"
            :row-price="rowRecordingPrice"
            :new-price="newRecordingPrice"
            @addPrice="addRecordingPrice"
            @pricesAdded="loadCx3Recording()"
          />
      </template>
    </ServiceScreenLayout>
  </div>
</template>

<script>
import ServiceScreenLayout from "@/layouts/ServiceScreenLayout";

import PriceFunctions from "@/components/Admin/PriceFunctions";
import AddPriceModal from "@/components/Admin/Modals/AddPriceModal";
import ModifyCx3RecordingPackageModal from "@/components/Admin/Modals/ModifyCx3RecordingPackageModal";
import PricingFilterToolbar from "@/components/Admin/PricingFilterToolbar";

export default {
  extends: PriceFunctions,
  components: {
      ServiceScreenLayout,
      ModifyCx3RecordingPackageModal,
      AddPriceModal,
      PricingFilterToolbar,
  },
  props: {
    productNumber: {
      type: Number,
      required: true,
    },
    userEditRight: {
      type: Boolean,
      required: true
    },
  },
  data() {
    return {
      parts: [
        {
          name: "call",
          icon: "fa fa-phone",
          displayName: "Call Vault Packages",
          permissions: ["STAFF"],
        },
      ],
      cx3Recording: [],
      rowRecordingPrice: [],
      priceRecordingPackage: {},
      newRecordingPrice: {},
      cx3RecordData: [],
      period: this.getCurrentPeriod(),
      recordExpiredFilter: "false",
      recordSearchTerm: "",
    };
  },
  mounted() {
    this.loadCx3Recording();
  },
  computed: {
    recordingPackages() {
      return this.filterPricingPackages(
        this.cx3Recording,
        this.recordExpiredFilter,
        this.recordSearchTerm,
        ["hours"]
      );
    },
  },
  methods: {
    filterPricingPackages(packages, expiredFilter, searchTerm, extraFields = []) {
      let filteredPackages = packages;

      if (expiredFilter === "false") {
        filteredPackages = filteredPackages.filter((pkg) => pkg.expired === false);
      } else if (expiredFilter === "true") {
        filteredPackages = filteredPackages.filter((pkg) => pkg.expired === true);
      }

      if (searchTerm) {
        const normalizedSearchTerm = searchTerm.toLowerCase();
        filteredPackages = filteredPackages.filter((pkg) =>
          [pkg.description, pkg.label, ...extraFields.map((field) => pkg[field])]
            .filter((value) => value !== null && value !== undefined && value !== "")
            .some((value) =>
              String(value).toLowerCase().includes(normalizedSearchTerm)
            )
        );
      }

      return filteredPackages;
    },
    loadCx3Recording() {
      this.$http
        .get(
          this.$config.aimsAPI +
            "pricing/packages/product/" +
            this.productNumber + "/CallCabinetPackage"
        )
        .then(
          (response) => {
            this.cx3Recording = response.data;
            this.setNewRecordingPrice();
          },
          (error) => {
            console.error(error);
            this.showError("Error Fetching 3CX Call Cabinet Packages", error);
          }
        );
    },
    setNewRecordingPrice() {
      this.$http
        .get(
          this.$config.aimsAPI +
            "pricing/new/price/CallCabinetPackagePrice"
        )
        .then(
          (response) => {
            this.newRecordingPrice = response.data;
          },
          (error) => {
            console.error(error);
            this.showError("Error Fetching Call Cabinet Price Template", error);
          }
        );
    },
    openRecordingPriceModal(record) {
      this.rowRecordingPrice = this.deepCopyObject(
        record.prices || []
      );
      this.newRecordingPrice.callCabinetPackageNumber = record.callCabinetPackageNumber;
      this.$refs.addRecordingPriceModal.open();
    },
    addRecordingPrice(newPrice) {
      this.rowRecordingPrice.forEach((price) => {
        if (price.validUntil == 0) {
          price.validUntil = this.decreaseBillingPeriod(newPrice.validFrom);
        }
      });
      this.rowRecordingPrice.push(newPrice);
      this.setNewRecordingPrice();
    },
    editRecording(record) {
      this.priceRecordingPackage = this.deepCopyObject(record);
      this.$refs.modifyRecordingPackageModal.open();
    },
    createNewRecording() {
      this.$http
        .get(
          this.$config.aimsAPI +
            "pricing/new/package/CallCabinetPackage"
        )
        .then(
          (response) => {
            this.priceRecordingPackage = response.data;
            this.priceRecordingPackage.productNumber = this.productNumber;
            this.$refs.modifyRecordingPackageModal.open();
          },
          (error) => {
            console.error(error);
            this.showError("Error Fetching Call Cabinet Template", error);
          }
        );
    },
    recordingAdded(record) {
      this.cx3Recording.push(record);
      this.openRecordingPriceModal(record);
    },

    deletePackage(cx3package) {
      this.$swal({
        title: "Are you sure you want to delete this Package?",
        type: "warning",
        showCancelButton: true,
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes",
      }).then((result) => {
        if (result.value) {
          this.$http
            .delete(
              this.$config.aimsAPI +
                "pricing/pricepackages/" +
                cx3package.objKey +
                "?objClassName=" +
                cx3package.class
            )
            .then(
              (response) => {
                this.showSuccess(response.data.message);
                this.loadCx3Recording();
              },
              (error) => {
                this.showError("Error Deleting Price Package", error);
              }
            );
        }
      });
    },
  },
};
</script>
